import React, { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';

// One tab / segmented-control style for the app (there were four).
//
//   <Tabs tabs={[{ id: 'all', label: 'All', count: 12 }]} value={tab} onChange={setTab} />
//   variant="underline"  page-level tabs under a header
//   variant="segmented"  small switches inside a toolbar (status, view)
//
// Proper roles (tablist / tab / aria-selected) and arrow-key movement between tabs.
export default function Tabs({ tabs = [], value, onChange, variant = 'underline', ariaLabel = 'Sections', className, size = 'md' }) {
  const refs = useRef([]);
  const list = tabs.filter(Boolean);
  const onKey = (e, i) => {
    let n = null;
    if (e.key === 'ArrowRight') n = (i + 1) % list.length;
    else if (e.key === 'ArrowLeft') n = (i - 1 + list.length) % list.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = list.length - 1;
    if (n == null) return;
    e.preventDefault();
    const t = list[n];
    if (t && !t.disabled) { onChange && onChange(t.id); refs.current[n] && refs.current[n].focus(); }
  };

  if (variant === 'segmented') {
    return (
      <div role="tablist" aria-label={ariaLabel}
        className={clsx('inline-flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5', className)}>
        {list.map((t, i) => {
          const on = t.id === value;
          return (
            <button key={t.id} ref={(el) => { refs.current[i] = el; }} type="button" role="tab" aria-selected={on}
              tabIndex={on ? 0 : -1} disabled={t.disabled} onClick={() => onChange && onChange(t.id)} onKeyDown={(e) => onKey(e, i)}
              className={clsx('inline-flex items-center gap-1.5 rounded-md px-3 font-medium whitespace-nowrap transition-colors duration-150',
                size === 'sm' ? 'h-7 text-[12px]' : 'h-8 text-[13px]',
                on ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(16,24,40,.08)]' : 'text-slate-600 hover:text-slate-900',
                'disabled:opacity-40 disabled:cursor-not-allowed')}>
              {t.icon && <t.icon size={14} className={on ? 'text-primary-600' : 'text-slate-400'} />}
              {t.label}
              {t.count != null && <span className={clsx('tabular-nums text-[11px] font-semibold', on ? 'text-slate-500' : 'text-slate-400')}>{t.count}</span>}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div role="tablist" aria-label={ariaLabel}
      className={clsx('flex items-end gap-1 overflow-x-auto border-b border-slate-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', className)}>
      {list.map((t, i) => {
        const on = t.id === value;
        return (
          <button key={t.id} ref={(el) => { refs.current[i] = el; }} type="button" role="tab" aria-selected={on}
            tabIndex={on ? 0 : -1} disabled={t.disabled} onClick={() => onChange && onChange(t.id)} onKeyDown={(e) => onKey(e, i)}
            className={clsx('-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors duration-150',
              on ? 'border-primary-600 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300',
              'disabled:opacity-40 disabled:cursor-not-allowed')}>
            {t.icon && <t.icon size={15} className={on ? 'text-primary-600' : 'text-slate-400'} />}
            {t.label}
            {t.count != null && (
              <span className={clsx('rounded-full px-1.5 text-[11px] font-semibold tabular-nums', on ? 'bg-primary-50 text-primary-700' : 'bg-slate-100 text-slate-500')}>{t.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// The active tab lives in the page address (?tab=), so Back, refresh and a shared link all land on
// the same tab. Changing tab pushes history; `replace` only for the first defaulting.
export function useUrlTab(param = 'tab', fallback = '', allowed) {
  const [params, setParams] = useSearchParams();
  const raw = params.get(param);
  const value = raw && (!allowed || allowed.includes(raw)) ? raw : fallback;
  const set = useCallback((next) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (!next || next === fallback) p.delete(param); else p.set(param, next);
      return p;
    });
  }, [param, fallback, setParams]);
  return [value, set];
}
